import { useCallback, useEffect, useMemo, useState } from "react";

import { useTranslation } from "react-i18next";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Loader2, Mail, Phone, MapPin, Calendar, ShieldAlert, Wallet, Pencil, Save, X, UserPlus, Trash2, Search, Send, Link as LinkIcon, Copy, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { fmtMoney } from "@/utils/money";
import { fmtDateTime, fmtDate } from "@/utils/datetime";
import { DocumentsSection } from "@/components/documents/DocumentsSection";
import { GuardianNotifyCard } from "@/components/notifications/GuardianNotifyCard";
import { WhatsAppButton } from "@/components/whatsapp/WhatsAppButton";

type Person = {
    id: string;
    branch_id: string | null;
    first_name: string;
    last_name: string;
    dob: string | null;
    gender: string | null;
    email: string | null;
    phone: string | null;
    phone_alt: string | null;
    address: string | null;
    notes: string | null;
    has_sensitive_notes: boolean;
    person_types: string[];
    id_type: string | null;
    id_number: string | null;
    is_active: boolean;
    created_at: string | null;
};

type Guardianship = {
    id: string;
    relation: string;
    is_primary_contact: boolean;
    is_payer: boolean;
    person: { id: string; first_name: string; last_name: string; phone: string | null; email: string | null } | null;
};

type Enrollment = {
    id: string;
    person_id: string;
    program_id: string;
    group_id: string | null;
    status: string;
    is_waitlist: boolean;
    waitlist_position: number | null;
    start_date: string | null;
    end_date: string | null;
    notes: string | null;
};

type EnrollmentEvent = {
    id: string;
    enrollment_id: string;
    event_type: string;
    from_status: string | null;
    to_status: string | null;
    from_group_id: string | null;
    to_group_id: string | null;
    note: string | null;
    at: string;
};

type AttSummary = { total_sessions: number; attended: number; cancelled_excluded: number; pct: number | null };

type TabKey = "overview" | "guardianships" | "enrollments" | "attendance" | "finance" | "history";

export default function PersonDetail() {
    const { t } = useTranslation();
    const { id } = useParams();
    const nav = useNavigate();
    const { hasPerm } = useAuth();
    const canView = hasPerm("people.view");
    const canEdit = hasPerm("people.edit");
    const canFinance = hasPerm("finance.view");

    const [person, setPerson] = useState<Person | null>(null);
    const [gships, setGships] = useState<{ as_child: Guardianship[]; as_guardian: Guardianship[] }>({ as_child: [], as_guardian: [] });
    const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
    const [attSummary, setAttSummary] = useState<AttSummary | null>(null);
    const [finance, setFinance] = useState<any | null>(null);
    const [history, setHistory] = useState<(EnrollmentEvent & { enrollment_id: string })[]>([]);
    const [tab, setTab] = useState<TabKey>("overview");
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState<string | null>(null);
    const [permDenied, setPermDenied] = useState(false);

    const load = useCallback(async () => {
        if (!id) return;
        setLoading(true); setErr(null); setPermDenied(false);
        try {
            const p = await api.get(`/people/${id}`);
            setPerson(p.data);
            const [gs, en, as_, fin] = await Promise.all([
                api.get(`/people/${id}/guardianships`).catch(() => ({ data: { as_child: [], as_guardian: [] } })),
                api.get(`/enrollments?person_id=${id}`).catch(() => ({ data: { items: [] } })),
                api.get(`/attendance/person/${id}/summary`).catch(() => ({ data: null })),
                canFinance ? api.get(`/finance/person/${id}/summary`).catch(() => ({ data: null })) : Promise.resolve({ data: null }),
            ]);
            setGships(gs.data);
            const enrolls: Enrollment[] = en.data.items || [];
            setEnrollments(enrolls);
            setAttSummary(as_.data);
            setFinance(fin.data);
            if (enrolls.length > 0) {
                const results = await Promise.all(
                    enrolls.map((e) =>
                        api.get(`/enrollments/${e.id}/history`).then((r) => (r.data.items as EnrollmentEvent[]).map((it) => ({ ...it, enrollment_id: e.id }))).catch(() => [])
                    )
                );
                const merged = results.flat().sort((a, b) => (a.at < b.at ? 1 : -1));
                setHistory(merged);
            } else {
                setHistory([]);
            }
        } catch (e: any) {
            const code = e?.response?.status;
            if (code === 403) setPermDenied(true);
            else setErr(formatApiError(e));
        } finally {
            setLoading(false);
        }
    }, [id, canFinance]);

    useEffect(() => { load(); }, [load]);

    if (!canView) {
        return (
            <div className="surface-card p-8 text-center" data-testid="person-perm-denied">
                <ShieldAlert size={32} className="mx-auto mb-3" style={{ color: "var(--warning-fg)" }} />
                <p className="text-sm" style={{ color: "var(--text)" }}>{t("common.no_permission")}</p>
            </div>
        );
    }
    if (permDenied) {
        return (
            <div className="surface-card p-8 text-center" data-testid="person-perm-denied">
                <ShieldAlert size={32} className="mx-auto mb-3" style={{ color: "var(--warning-fg)" }} />
                <p className="text-sm" style={{ color: "var(--text)" }}>{t("common.no_permission")}</p>
                <button onClick={() => nav("/people")} className="mt-4 text-xs" style={{ color: "var(--accent)" }}>{t("common.back")}</button>
            </div>
        );
    }
    if (loading || !person) return <div className="flex justify-center p-8"><Loader2 className="animate-spin" /></div>;
    if (err) return <div className="rounded-md p-3 text-sm" style={{ background: "var(--error-bg)", color: "var(--error-fg)" }} data-testid="person-error">{err}</div>;

    const isStudent = person.person_types.includes("student");
    const tabs: { key: TabKey; label: string; testid: string }[] = [
        { key: "overview", label: t("person.tab.overview"), testid: "tab-overview" },
        { key: "guardianships", label: t("person.tab.guardianships"), testid: "tab-guardianships" },
        { key: "enrollments", label: t("person.tab.enrollments"), testid: "tab-enrollments" },
        { key: "attendance", label: t("person.tab.attendance"), testid: "tab-attendance" },
        ...(canFinance ? [{ key: "finance" as TabKey, label: t("person.tab.finance"), testid: "tab-finance" }] : []),
        { key: "history", label: t("person.tab.history"), testid: "tab-history" },
    ];

    return (
        <div className="space-y-6" data-testid="person-detail-page">
            <button onClick={() => nav(-1)} className="inline-flex items-center gap-1 text-sm" style={{ color: "var(--text-muted)" }} data-testid="back-btn">
                <ArrowLeft size={14} /> {t("common.back")}
            </button>
            <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-semibold" style={{ color: "var(--text)" }} data-testid="page-title">
                        {person.first_name} {person.last_name}
                    </h1>
                    <div className="flex flex-wrap gap-2 mt-2">
                        {person.person_types.map((tp) => (
                            <span key={tp} className="status-pill status-info" data-testid={`type-pill-${tp}`}>
                                {t(`people.type.${tp}` as any, { defaultValue: tp })}
                            </span>
                        ))}
                        <span className={`status-pill ${person.is_active ? "status-success" : "status-neutral"}`}>
                            {person.is_active ? t("common.active") : t("common.archived")}
                        </span>
                    </div>
                </div>
            </div>
            <div className="flex gap-1 border-b overflow-x-auto" style={{ borderColor: "var(--border)" }}>
                {tabs.map((tb) => (
                    <button
                        key={tb.key}
                        data-testid={tb.testid}
                        onClick={() => setTab(tb.key)}
                        className="px-4 py-2 text-sm border-b-2 transition-colors whitespace-nowrap"
                        style={{
                            color: tab === tb.key ? "var(--text)" : "var(--text-muted)",
                            borderColor: tab === tb.key ? "var(--primary)" : "transparent",
                        }}
                    >
                        {tb.label}
                    </button>
                ))}
            </div>
            {tab === "overview" && <OverviewTab person={person} canEdit={canEdit} onSaved={load} />}
            {tab === "overview" && (person.person_types.includes("guardian") || person.person_types.includes("payer")) && (
                <PortalInviteCard person={person} canEdit={canEdit} />
            )}
            {tab === "overview" && person.person_types.includes("guardian") && <GuardianNotifyCard personId={person.id} />}
            {tab === "overview" && <DocumentsSection entityType="person" entityId={person.id} isStudent={isStudent} />}
            {tab === "guardianships" && <GuardianshipsTab gships={gships} personId={person.id} isStudent={isStudent} canEdit={canEdit} onChanged={load} />}
            {tab === "enrollments" && <EnrollmentsTab items={enrollments} />}
            {tab === "attendance" && <AttendanceTab summary={attSummary} />}
            {tab === "finance" && <FinanceTab summary={finance} />}
            {tab === "history" && <HistoryTab items={history} />}
        </div>
    );
}

function OverviewTab({ person, canEdit, onSaved }: { person: Person; canEdit: boolean; onSaved: () => void }) {
    const { t } = useTranslation();
    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState<string | null>(null);
    const [form, setForm] = useState({
        dob: person.dob || "",
        gender: person.gender || "",
        phone: person.phone || "",
        address: person.address || "",
    });
    useEffect(() => {
        setForm({ dob: person.dob || "", gender: person.gender || "", phone: person.phone || "", address: person.address || "" });
    }, [person.id, person.dob, person.gender, person.phone, person.address]);

    const submit = async () => {
        setSaving(true); setErr(null);
        try {
            const payload: any = {
                dob: form.dob || null,
                gender: form.gender || null,
                phone: form.phone || null,
                address: form.address || null,
            };
            await api.patch(`/people/${person.id}`, payload);
            toast.success(t("person.saved"));
            setEditing(false);
            onSaved();
        } catch (e: any) {
            const detail = e?.response?.data?.detail;
            if (Array.isArray(detail) && detail.some((d: any) => String(d.msg || "").includes("dob_in_future"))) {
                setErr(t("person.err.dob_in_future"));
            } else {
                setErr(formatApiError(e));
            }
        } finally {
            setSaving(false);
        }
    };

    if (editing) {
        return (
            <div className="surface-card p-6 space-y-4" data-testid="overview-edit">
                <div className="flex items-center justify-between">
                    <div className="text-sm font-medium" style={{ color: "var(--text)" }}>{t("person.edit")}</div>
                    <button data-testid="overview-cancel" onClick={() => { setEditing(false); setErr(null); }} className="text-xs inline-flex items-center gap-1" style={{ color: "var(--text-muted)" }}>
                        <X size={12} /> {t("person.cancel")}
                    </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FieldEdit label={t("person.dob")} testid="edit-dob">
                        <input data-testid="input-dob" type="date" max={new Date().toISOString().slice(0, 10)} value={form.dob || ""} onChange={(e) => setForm({ ...form, dob: e.target.value })} className="surface-input px-3 py-2 w-full text-sm" style={{ minHeight: 40 }} />
                    </FieldEdit>
                    <FieldEdit label={t("person.gender")} testid="edit-gender">
                        <select data-testid="input-gender" value={form.gender || ""} onChange={(e) => setForm({ ...form, gender: e.target.value })} className="surface-input px-3 py-2 w-full text-sm" style={{ minHeight: 40 }}>
                            <option value="">—</option>
                            <option value="female">{t("person.gender.female")}</option>
                            <option value="male">{t("person.gender.male")}</option>
                            <option value="other">{t("person.gender.other")}</option>
                        </select>
                    </FieldEdit>
                    <FieldEdit label={t("people.phone")} testid="edit-phone">
                        <input data-testid="input-phone" value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="surface-input px-3 py-2 w-full text-sm" style={{ minHeight: 40 }} />
                    </FieldEdit>
                    <FieldEdit label={t("person.address")} testid="edit-address">
                        <input data-testid="input-address" value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} className="surface-input px-3 py-2 w-full text-sm" style={{ minHeight: 40 }} />
                    </FieldEdit>
                </div>
                {err && <div className="text-sm rounded-md p-2" data-testid="overview-edit-err" style={{ background: "var(--error-bg)", color: "var(--error-fg)" }}>{err}</div>}
                <button data-testid="overview-save-btn" disabled={saving} onClick={submit} className="inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium disabled:opacity-60" style={{ background: "var(--primary)", color: "var(--primary-fg)", minHeight: 40 }}>
                    {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} {t("person.save")}
                </button>
            </div>
        );
    }

    const rows: { label: string; value: string | null; icon?: React.ReactNode; testid: string }[] = [
        { label: t("auth.email"), value: person.email, icon: <Mail size={14} />, testid: "row-email" },
        { label: t("people.phone"), value: person.phone, icon: <Phone size={14} />, testid: "row-phone" },
        { label: t("person.phone_alt"), value: person.phone_alt, icon: <Phone size={14} />, testid: "row-phone-alt" },
        { label: t("person.dob"), value: person.dob, icon: <Calendar size={14} />, testid: "row-dob" },
        { label: t("person.gender"), value: person.gender ? t(`person.gender.${person.gender}` as any, { defaultValue: person.gender }) : null, testid: "row-gender" },
        { label: t("person.address"), value: person.address, icon: <MapPin size={14} />, testid: "row-address" },
        { label: t("person.id_type"), value: person.id_type, testid: "row-id-type" },
        { label: t("person.id_number"), value: person.id_number, testid: "row-id-number" },
    ];
    return (
        <div className="surface-card p-6 space-y-4" data-testid="overview-tab">
            {canEdit && (
                <div className="flex justify-end">
                    <button data-testid="overview-edit-btn" onClick={() => setEditing(true)} className="inline-flex items-center gap-1 text-xs px-3 py-1 rounded-md border" style={{ borderColor: "var(--border)", color: "var(--text)" }}>
                        <Pencil size={12} /> {t("person.edit")}
                    </button>
                </div>
            )}
            <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {rows.map((r) => (
                    <div key={r.testid} data-testid={r.testid}>
                        <dt className="text-xs mb-1 flex items-center gap-1" style={{ color: "var(--text-muted)" }}>
                            {r.icon}{r.label}
                        </dt>
                        <dd className="text-sm flex items-center gap-2" style={{ color: "var(--text)" }}>{r.value || "—"}
                            {(r.testid === "row-phone" || r.testid === "row-phone-alt") && <WhatsAppButton name={`${person.first_name} ${person.last_name}`} phone={r.value} type="person" id={person.id} testid={`person-detail-wa-btn-${r.testid}`} />}
                        </dd>
                    </div>
                ))}
            </dl>
            {person.notes && (
                <div data-testid="row-notes">
                    <div className="text-xs mb-1" style={{ color: "var(--text-muted)" }}>{t("person.notes")}</div>
                    <p className="text-sm whitespace-pre-wrap" style={{ color: "var(--text)" }}>{person.notes}</p>
                </div>
            )}
            {person.has_sensitive_notes && !person.notes && (
                <div className="rounded-md p-3 text-xs inline-flex items-center gap-2" style={{ background: "var(--warning-bg)", color: "var(--warning-fg)" }} data-testid="sensitive-hidden">
                    <ShieldAlert size={14} /> {t("person.sensitive_hidden")}
                </div>
            )}
        </div>
    );
}

function FieldEdit({ label, testid, children }: { label: string; testid: string; children: React.ReactNode }) {
    return (
        <label className="block" data-testid={testid}>
            <div className="text-xs mb-1" style={{ color: "var(--text-muted)" }}>{label}</div>
            {children}
        </label>
    );
}

function PortalInviteCard({ person, canEdit }: { person: Person; canEdit: boolean }) {
    const { t } = useTranslation();
    const [status, setStatus] = useState<null | { has_invite: boolean; email: string | null; expires_at?: string; invite_id?: string; used?: boolean }>(null);
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const [lastResult, setLastResult] = useState<null | { email_status: string; fallback_link?: string | null; is_payer: boolean; reused: boolean; expires_at: string }>(null);
    const [err, setErr] = useState<string | null>(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/people/${person.id}/portal-invite`);
            setStatus(data);
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, [person.id]);

    const isPayer = person.person_types.includes("payer");
    const send = async () => {
        setSending(true); setErr(null); setLastResult(null);
        try {
            const { data } = await api.post(`/people/${person.id}/portal-invite`);
            setLastResult({ email_status: data.email_status, fallback_link: data.fallback_link, is_payer: data.is_payer, reused: data.reused, expires_at: data.expires_at });
            if (data.email_status === "sent") toast.success(data.reused ? t("portal.invite.reused") : t("portal.invite.sent_ok"));
            else toast.error(t("portal.invite.sent_fail"));
            load();
        } catch (e: any) {
            const detail = e?.response?.data?.detail;
            if (detail?.error === "email_required") setErr(t("portal.invite.no_email"));
            else if (detail?.error === "no_linked_students") setErr(t("portal.invite.need_students"));
            else if (detail?.error === "not_guardian_or_payer") setErr(t("portal.invite.need_students"));
            else setErr(formatApiError(e));
        } finally { setSending(false); }
    };
    const copyLink = async (url: string) => {
        try { await navigator.clipboard.writeText(url); toast.success(t("portal.invite.copied")); } catch { /* ignore */ }
    };

    const hasEmail = !!person.email;
    return (
        <div className="surface-card p-6 space-y-4" data-testid="portal-invite-card">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                    <LinkIcon size={16} style={{ color: "var(--primary)" }} />
                    <h3 className="text-sm font-semibold" style={{ color: "var(--text)" }}>{t("portal.invite.title")}</h3>
                </div>
                <span className="status-pill status-info" data-testid="portal-invite-role">{isPayer ? t("portal.title.payer") : t("portal.title")}</span>
            </div>
            <p className="text-xs" style={{ color: "var(--text-muted)" }}>{isPayer ? t("portal.invite.desc_payer") : t("portal.invite.desc_guardian")}</p>
            {loading ? (
                <div className="text-xs" style={{ color: "var(--text-muted)" }}><Loader2 className="animate-spin inline" size={12} /></div>
            ) : (
                <div className="space-y-3">
                    {!hasEmail && <div className="rounded-md p-2 text-xs" style={{ background: "var(--warning-bg)", color: "var(--warning-fg)" }} data-testid="invite-no-email">{t("portal.invite.no_email")}</div>}
                    {hasEmail && (
                        <div className="flex items-center gap-2 text-xs" style={{ color: "var(--text-muted)" }}>
                            <Mail size={12} /> <span data-testid="invite-email">{person.email}</span>
                        </div>
                    )}
                    {status?.has_invite && !status?.used && (
                        <div className="text-xs" data-testid="invite-status-pending">
                            <span className="status-pill status-warning"><CheckCircle2 size={10} className="inline mr-1" />{t("portal.invite.pending")}</span>
                            {status.expires_at && <span className="ml-2" style={{ color: "var(--text-muted)" }}>{t("portal.invite.expires", { when: fmtDate(status.expires_at) })}</span>}
                        </div>
                    )}
                    {lastResult && lastResult.email_status !== "sent" && lastResult.fallback_link && canEdit && (
                        <div className="rounded-md p-3 text-xs space-y-2" style={{ background: "var(--error-bg)", color: "var(--error-fg)" }} data-testid="invite-fallback">
                            <div>{t("portal.invite.sent_fail")}</div>
                            <div className="flex items-center gap-2 rounded-md px-2 py-1 break-all" style={{ background: "var(--card)", color: "var(--text)" }}>
                                <input readOnly value={lastResult.fallback_link} className="flex-1 bg-transparent outline-none text-xs" data-testid="invite-fallback-link" />
                                <button data-testid="invite-copy-btn" onClick={() => copyLink(lastResult.fallback_link!)} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md" style={{ background: "var(--primary)", color: "var(--primary-fg)" }}>
                                    <Copy size={10} /> {t("portal.invite.copy")}
                                </button>
                            </div>
                        </div>
                    )}
                    {err && <div className="rounded-md p-2 text-xs" style={{ background: "var(--error-bg)", color: "var(--error-fg)" }} data-testid="invite-err">{err}</div>}
                    {canEdit && (
                        <button data-testid="portal-invite-send-btn" disabled={sending || !hasEmail} onClick={send}
                            className="inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium disabled:opacity-60"
                            style={{ background: "var(--primary)", color: "var(--primary-fg)", minHeight: 40 }}>
                            {sending ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} />}
                            {status?.has_invite ? t("portal.invite.resend") : t("portal.invite.send")}
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}

function GuardianshipsTab({ gships, personId, isStudent, canEdit, onChanged }: {
    gships: { as_child: Guardianship[]; as_guardian: Guardianship[] };
    personId: string;
    isStudent: boolean;
    canEdit: boolean;
    onChanged: () => void;
}) {
    const { t } = useTranslation();
    const [showAdd, setShowAdd] = useState(false);

    const unlink = async (gid: string) => {
        if (!window.confirm(t("person.confirm_unlink"))) return;
        try {
            await api.delete(`/people/guardianships/${gid}`);
            toast.success(t("person.unlinked"));
            onChanged();
        } catch (e: any) {
            toast.error(formatApiError(e));
        }
    };

    const Section = ({ title, items, testid, allowUnlink }: { title: string; items: Guardianship[]; testid: string; allowUnlink: boolean }) => (
        <div className="surface-card overflow-hidden" data-testid={testid}>
            <div className="px-4 py-3 border-b text-sm font-medium" style={{ borderColor: "var(--border)", color: "var(--text)" }}>{title}</div>
            {items.length === 0 ? (
                <div className="p-6 text-sm text-center" style={{ color: "var(--text-muted)" }}>{t("common.empty")}</div>
            ) : (
                <ul className="divide-y" style={{ borderColor: "var(--border)" }}>
                    {items.map((g) => (
                        <li key={g.id} className="p-4 flex items-center justify-between gap-3 flex-wrap" data-testid={`gship-${g.id}`}>
                            <div>
                                {g.person ? (
                                    <Link to={`/people/${g.person.id}`} className="text-sm font-medium hover:underline" style={{ color: "var(--text)" }} data-testid={`gship-link-${g.id}`}>
                                        {g.person.first_name} {g.person.last_name}
                                    </Link>
                                ) : (
                                    <span className="text-sm" style={{ color: "var(--text-muted)" }}>—</span>
                                )}
                                <div className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>
                                    {g.person?.phone || ""} {g.person?.email ? `· ${g.person.email}` : ""}
                                </div>
                            </div>
                            <div className="flex gap-2 flex-wrap items-center">
                                <span className="status-pill status-neutral">{t(`person.relation.${g.relation}` as any, { defaultValue: g.relation })}</span>
                                {g.is_primary_contact && <span className="status-pill status-info">{t("person.primary_contact")}</span>}
                                {g.is_payer && <span className="status-pill status-success">{t("person.payer")}</span>}
                                {allowUnlink && canEdit && (
                                    <button data-testid={`gship-unlink-${g.id}`} onClick={() => unlink(g.id)} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border" style={{ borderColor: "var(--error-fg)", color: "var(--error-fg)" }}>
                                        <Trash2 size={12} /> {t("person.unlink")}
                                    </button>
                                )}
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
    return (
        <div className="space-y-4" data-testid="guardianships-tab">
            {isStudent && canEdit && (
                <div className="flex justify-end">
                    <button data-testid="add-guardian-btn" onClick={() => setShowAdd(true)} className="inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium" style={{ background: "var(--primary)", color: "var(--primary-fg)", minHeight: 40 }}>
                        <UserPlus size={14} /> {t("person.add_guardian")}
                    </button>
                </div>
            )}
            <Section title={t("person.as_child")} items={gships.as_child} testid="section-as-child" allowUnlink={true} />
            <Section title={t("person.as_guardian")} items={gships.as_guardian} testid="section-as-guardian" allowUnlink={true} />
            {showAdd && <AddGuardianDialog studentId={personId} onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); onChanged(); }} />}
        </div>
    );
}

function AddGuardianDialog({ studentId, onClose, onSaved }: { studentId: string; onClose: () => void; onSaved: () => void }) {
    const { t } = useTranslation();
    const [q, setQ] = useState("");
    const [people, setPeople] = useState<{ id: string; first_name: string; last_name: string; person_types: string[] }[]>([]);
    const [loading, setLoading] = useState(false);
    const [selected, setSelected] = useState<string | null>(null);
    const [relation, setRelation] = useState("other");
    const [isPayer, setIsPayer] = useState(false);
    const [isPrimary, setIsPrimary] = useState(false);
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState<string | null>(null);

    useEffect(() => {
        let alive = true;
        const run = async () => {
            setLoading(true);
            try {
                const params = new URLSearchParams({ limit: "30" });
                if (q) params.set("q", q);
                const { data } = await api.get(`/people?${params.toString()}`);
                if (alive) {
                    const items = (data.items || []) as any[];
                    setPeople(items.filter((p) => p.id !== studentId));
                }
            } finally { if (alive) setLoading(false); }
        };
        const h = setTimeout(run, 200);
        return () => { alive = false; clearTimeout(h); };
    }, [q, studentId]);

    const submit = async () => {
        if (!selected) return;
        setSaving(true); setErr(null);
        try {
            await api.post(`/people/${studentId}/guardianships`, {
                guardian_person_id: selected,
                relation,
                is_payer: isPayer,
                is_primary_contact: isPrimary,
            });
            toast.success(t("person.linked"));
            onSaved();
        } catch (e: any) {
            setErr(formatApiError(e));
        } finally { setSaving(false); }
    };

    const selectedPerson = useMemo(() => people.find((p) => p.id === selected) || null, [people, selected]);

    return (
        <div className="fixed inset-0 z-40 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.5)" }} onClick={onClose} data-testid="add-guardian-dialog">
            <div className="surface-card w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold" style={{ color: "var(--text)" }}>{t("person.add_guardian")}</h2>
                    <button data-testid="add-guardian-close" onClick={onClose} style={{ color: "var(--text-muted)" }}><X size={18} /></button>
                </div>
                <div className="space-y-3">
                    <label className="flex items-center gap-2 rounded-md px-3 border" style={{ background: "var(--input)", borderColor: "var(--border)", height: 40 }}>
                        <Search size={14} style={{ color: "var(--text-muted)" }} />
                        <input data-testid="add-guardian-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("person.pick_person")}
                            className="bg-transparent outline-none text-sm w-full" style={{ color: "var(--text)" }} />
                    </label>
                    <div className="max-h-48 overflow-y-auto rounded-md border" style={{ borderColor: "var(--border)" }}>
                        {loading ? <div className="p-3 text-center"><Loader2 className="animate-spin inline" size={14} /></div>
                            : people.length === 0 ? <div className="p-3 text-xs text-center" style={{ color: "var(--text-muted)" }}>{t("common.empty")}</div>
                            : (
                                <ul className="divide-y" style={{ borderColor: "var(--border)" }}>
                                    {people.map((p) => (
                                        <li key={p.id}>
                                            <button type="button" data-testid={`add-guardian-pick-${p.id}`} onClick={() => setSelected(p.id)}
                                                className="w-full text-left px-3 py-2 text-sm flex items-center justify-between"
                                                style={{ color: "var(--text)", background: selected === p.id ? "var(--selected-bg)" : "transparent" }}>
                                                <span>{p.first_name} {p.last_name}</span>
                                                <span className="flex gap-1">{p.person_types.map((tp) => <span key={tp} className="status-pill status-info">{t(`people.type.${tp}` as any, { defaultValue: tp })}</span>)}</span>
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                    </div>
                    {selectedPerson && (
                        <div className="rounded-md p-3 border" style={{ borderColor: "var(--border)", background: "var(--input)" }}>
                            <div className="text-xs mb-2" style={{ color: "var(--text-muted)" }}>{t("person.selected_students")}: <b style={{ color: "var(--text)" }}>{selectedPerson.first_name} {selectedPerson.last_name}</b></div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <label className="block">
                                    <div className="text-xs mb-1" style={{ color: "var(--text-muted)" }}>{t("person.relation.other")}</div>
                                    <select data-testid="add-guardian-relation" value={relation} onChange={(e) => setRelation(e.target.value)} className="surface-input px-3 py-2 w-full text-sm" style={{ minHeight: 40 }}>
                                        <option value="mother">{t("person.relation.mother")}</option>
                                        <option value="father">{t("person.relation.father")}</option>
                                        <option value="grandparent">{t("person.relation.grandparent")}</option>
                                        <option value="other">{t("person.relation.other")}</option>
                                    </select>
                                </label>
                                <div className="flex flex-col gap-2 pt-4">
                                    <label className="inline-flex items-center gap-2 text-sm" style={{ color: "var(--text)" }}>
                                        <input data-testid="add-guardian-is-payer" type="checkbox" checked={isPayer} onChange={(e) => setIsPayer(e.target.checked)} /> {t("person.payer")}
                                    </label>
                                    <label className="inline-flex items-center gap-2 text-sm" style={{ color: "var(--text)" }}>
                                        <input data-testid="add-guardian-is-primary" type="checkbox" checked={isPrimary} onChange={(e) => setIsPrimary(e.target.checked)} /> {t("person.primary_contact")}
                                    </label>
                                </div>
                            </div>
                        </div>
                    )}
                    {err && <div className="text-sm rounded-md p-2" data-testid="add-guardian-err" style={{ background: "var(--error-bg)", color: "var(--error-fg)" }}>{err}</div>}
                    <button data-testid="add-guardian-save-btn" disabled={!selected || saving} onClick={submit} className="w-full inline-flex justify-center items-center gap-2 rounded-md py-2 text-sm font-medium disabled:opacity-60" style={{ background: "var(--primary)", color: "var(--primary-fg)", minHeight: 44 }}>
                        {saving ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />} {t("person.save")}
                    </button>
                </div>
            </div>
        </div>
    );
}

function EnrollmentsTab({ items }: { items: Enrollment[] }) {
    const { t } = useTranslation();
    return (
        <div className="surface-card overflow-hidden" data-testid="enrollments-tab">
            {items.length === 0 ? (
                <div className="p-6 text-sm text-center" style={{ color: "var(--text-muted)" }}>{t("common.empty")}</div>
            ) : (
                <table className="w-full text-sm">
                    <thead>
                        <tr className="text-left" style={{ background: "var(--input)", color: "var(--text-muted)" }}>
                            <th className="px-4 py-2 font-medium">{t("nav.groups")}</th>
                            <th className="px-4 py-2 font-medium">{t("common.status")}</th>
                            <th className="px-4 py-2 font-medium">{t("person.start_date")}</th>
                            <th className="px-4 py-2 font-medium">{t("person.end_date")}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((e) => (
                            <tr key={e.id} className="border-t" style={{ borderColor: "var(--border)" }} data-testid={`enrollment-row-${e.id}`}>
                                <td className="px-4 py-3">
                                    {e.group_id ? (
                                        <Link to={`/groups/${e.group_id}`} className="hover:underline" style={{ color: "var(--text)" }} data-testid={`enrollment-group-link-${e.id}`}>
                                            {e.group_id.slice(0, 8)}…
                                        </Link>
                                    ) : (
                                        <span style={{ color: "var(--text-muted)" }}>—</span>
                                    )}
                                </td>
                                <td className="px-4 py-3">
                                    {e.is_waitlist ? (
                                        <span className="status-pill status-warning">{t("enrollment.waitlist")}{e.waitlist_position ? ` #${e.waitlist_position}` : ""}</span>
                                    ) : (
                                        <span className="status-pill status-success">{t(`enrollment.status.${e.status}` as any, { defaultValue: e.status })}</span>
                                    )}
                                </td>
                                <td className="px-4 py-3" style={{ color: "var(--text-muted)" }}>{e.start_date || "—"}</td>
                                <td className="px-4 py-3" style={{ color: "var(--text-muted)" }}>{e.end_date || "—"}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
}

function AttendanceTab({ summary }: { summary: AttSummary | null }) {
    const { t } = useTranslation();
    if (!summary) {
        return <div className="surface-card p-6 text-sm text-center" style={{ color: "var(--text-muted)" }} data-testid="attendance-tab">{t("common.empty")}</div>;
    }
    const Kpi = ({ label, value, testid }: { label: string; value: string | number; testid: string }) => (
        <div className="surface-card p-4" data-testid={testid}>
            <div className="text-xs mb-2" style={{ color: "var(--text-muted)" }}>{label}</div>
            <div className="text-2xl font-semibold" style={{ color: "var(--text)" }}>{value}</div>
        </div>
    );
    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4" data-testid="attendance-tab">
            <Kpi label={t("person.att.pct")} value={summary.pct !== null ? `${summary.pct}%` : "—"} testid="att-kpi-pct" />
            <Kpi label={t("person.att.attended")} value={summary.attended} testid="att-kpi-attended" />
            <Kpi label={t("person.att.total")} value={summary.total_sessions} testid="att-kpi-total" />
            <Kpi label={t("person.att.cancelled_excluded")} value={summary.cancelled_excluded} testid="att-kpi-cancelled" />
        </div>
    );
}

function HistoryTab({ items }: { items: (EnrollmentEvent & { enrollment_id: string })[] }) {
    const { t } = useTranslation();
    const formatted = useMemo(() => items.map((it) => ({ ...it, when: fmtDateTime(it.at) })), [items]);
    return (
        <div className="surface-card overflow-hidden" data-testid="history-tab">
            {formatted.length === 0 ? (
                <div className="p-6 text-sm text-center" style={{ color: "var(--text-muted)" }}>{t("common.empty")}</div>
            ) : (
                <ul className="divide-y" style={{ borderColor: "var(--border)" }}>
                    {formatted.map((it) => (
                        <li key={it.id} className="p-4 flex items-start gap-3" data-testid={`history-item-${it.id}`}>
                            <div className="mt-1 w-2 h-2 rounded-full" style={{ background: "var(--primary)" }} />
                            <div className="flex-1 min-w-0">
                                <div className="text-sm" style={{ color: "var(--text)" }}>
                                    {t(`person.event.${it.event_type}` as any, { defaultValue: it.event_type })}
                                    {it.from_status && it.to_status ? ` · ${it.from_status} → ${it.to_status}` : ""}
                                </div>
                                {it.note && <div className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>{it.note}</div>}
                                <div className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>{it.when}</div>
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

function FinanceTab({ summary }: { summary: any | null }) {
    const { t } = useTranslation();
    if (!summary) return <div className="surface-card p-6 text-sm text-center" style={{ color: "var(--text-muted)" }} data-testid="finance-tab">{t("common.empty")}</div>;
    const currency = summary.credit_currency || "AZN";
    return (
        <div className="space-y-4" data-testid="finance-tab">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="surface-card p-4" data-testid="fin-kpi-debt">
                    <div className="text-xs mb-2" style={{ color: "var(--text-muted)" }}>{t("person.finance.total_debt")}</div>
                    <div className="text-2xl font-semibold tabular-nums" style={{ color: summary.total_debt_minor > 0 ? "var(--error-fg)" : "var(--success-fg)" }}>{fmtMoney(summary.total_debt_minor, currency)}</div>
                </div>
                <div className="surface-card p-4" data-testid="fin-kpi-credit">
                    <div className="text-xs mb-2 flex items-center gap-1" style={{ color: "var(--text-muted)" }}><Wallet size={12}/> {t("person.finance.credit_balance")}</div>
                    <div className="text-2xl font-semibold tabular-nums" style={{ color: "var(--info-fg)" }}>{fmtMoney(summary.credit_balance_minor, currency)}</div>
                </div>
            </div>
            {summary.invoices?.length > 0 && (
                <div className="surface-card overflow-hidden">
                    <div className="px-4 py-2 border-b text-xs" style={{ borderColor: "var(--border)", color: "var(--text-muted)" }}>{t("finance.invoices.title")}</div>
                    <table className="w-full text-sm">
                        <tbody>
                            {summary.invoices.map((inv: any) => (
                                <tr key={inv.id} className="border-t" style={{ borderColor: "var(--border)" }} data-testid={`fin-inv-${inv.id}`}>
                                    <td className="px-4 py-2 font-mono text-xs" style={{ color: "var(--text)" }}>{inv.invoice_number}</td>
                                    <td className="px-4 py-2" style={{ color: "var(--text-muted)" }}>{inv.due_date}</td>
                                    <td className="px-4 py-2 text-right tabular-nums" style={{ color: "var(--text)" }}>{fmtMoney(inv.effective_total_minor, inv.currency)}</td>
                                    <td className="px-4 py-2 text-right tabular-nums" style={{ color: inv.debt_minor > 0 ? "var(--error-fg)" : "var(--success-fg)" }}>{fmtMoney(inv.debt_minor, inv.currency)}</td>
                                    <td className="px-4 py-2"><span className={`status-pill ${inv.status === "confirmed" ? "status-success" : inv.status === "void" ? "status-neutral" : "status-warning"}`}>{t(`finance.invoice.status.${inv.status}` as any)}</span></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            {summary.payments?.length > 0 && (
                <div className="surface-card overflow-hidden">
                    <div className="px-4 py-2 border-b text-xs" style={{ borderColor: "var(--border)", color: "var(--text-muted)" }}>{t("finance.payments.title")}</div>
                    <table className="w-full text-sm">
                        <tbody>
                            {summary.payments.map((p: any) => (
                                <tr key={p.id} className="border-t" style={{ borderColor: "var(--border)" }} data-testid={`fin-pay-${p.id}`}>
                                    <td className="px-4 py-2 font-mono text-xs" style={{ color: "var(--text)" }}>{p.receipt_number}</td>
                                    <td className="px-4 py-2" style={{ color: "var(--text-muted)" }}>{fmtDate(p.paid_at)}</td>
                                    <td className="px-4 py-2"><span className="status-pill status-info">{t(`finance.method.${p.method}` as any)}</span></td>
                                    <td className="px-4 py-2 text-right tabular-nums" style={{ color: "var(--text)" }}>{fmtMoney(p.amount_minor, p.currency)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
